import { useCurrentMessageCitations } from '@/lib/citation-store';
import { Badge, cn } from '@iq/ui';
import { ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Citation, CitationCard } from './citation-card';
import { sourceDisplayName } from './source-display-name';

/**
 * Sources strip rendered once per assistant message, below the message
 * body. Replaces the prior inline `[ID:N]` chip-per-marker render that
 * stuttered with repeated chips on Type B (troubleshooting) answers
 * citing a single source on every bullet.
 *
 * Empty when the message has no citations (refusals, small-talk).
 */
export function CitationFooter() {
  const { t } = useTranslation();
  const citations = useCurrentMessageCitations();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (citations.length === 0) return null;

  // Only nudge toward source images when at least one citation actually
  // resolved to an image (the KB is image-heavy, so this is the common case);
  // text-only-chunk turns must not claim "source images".
  const hasImageSource = citations.some((c) => c.type === 'image');

  return (
    <div className="mt-3 flex flex-col gap-1.5" data-slot="citation-footer">
      {hasImageSource && (
        <p
          data-slot="citation-image-hint"
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          <ImageIcon aria-hidden className="size-3.5 shrink-0" />
          {t('chat.citation.imageHint')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('chat.citation.sourcesLabel')}
        </span>
        {citations.map((raw, i) => {
          const index = i + 1;
          const citation: Citation = {
            index,
            docId: raw.documentId ?? '',
            ...(raw.documentTitle ? { docName: raw.documentTitle } : {}),
            ...(raw.quote ? { chunk: raw.quote } : {}),
            ...(raw.type === 'image' ? { imageRef: raw.ref } : {}),
            ...(raw.kbId ? { kbId: raw.kbId } : {}),
          };
          const label =
            sourceDisplayName(citation.docName, citation.chunk) ?? raw.documentId ?? raw.ref;
          return (
            <span key={`${citation.docId}-${index}`} className="inline-flex">
              <button
                type="button"
                onClick={() => setOpenIndex(index)}
                className="inline-flex items-center focus:outline-none"
                data-testid={`citation-source-${index}`}
                aria-label={t('chat.citation.source', { index })}
              >
                <Badge
                  variant="outline"
                  className={cn('cursor-pointer gap-1 text-xs hover:bg-accent')}
                >
                  <span className="text-muted-foreground">[{index}]</span>
                  <span className="max-w-[14rem] truncate">{label}</span>
                </Badge>
              </button>
              <CitationCard
                citation={citation}
                open={openIndex === index}
                onOpenChange={(o) => setOpenIndex(o ? index : null)}
              />
            </span>
          );
        })}
      </div>
    </div>
  );
}
